import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
interface CalendarViewProps {
  workouts: any[];
  onDayClick?: (day: number) => void;
  navigateTo?: (screen: string) => void;
}
export const CalendarView = ({
  workouts,
  onDayClick,
  navigateTo
}: CalendarViewProps) => {
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  // Generate years from 1900 to 2025
  const years = Array.from({
    length: 2025 - 1900 + 1
  }, (_, i) => 1900 + i);
  // Get current month and year
  const currentMonth = months[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();
  const formattedDate = `${currentMonth} ${currentYear}`;
  // Generate calendar days for the current month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return {
      daysInMonth,
      firstDayOfMonth
    };
  };
  const {
    daysInMonth,
    firstDayOfMonth
  } = getDaysInMonth(currentDate);
  // Calendar days including empty slots for proper alignment
  const calendarDays = Array(daysInMonth).fill(0).map((_, i) => i + 1);
  // Days of the week
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Map workouts to days for the current month
  const workoutsByDay = workouts.reduce((acc, workout) => {
    const workoutDate = new Date(workout.date);
    const workoutMonth = workoutDate.getMonth();
    const workoutYear = workoutDate.getFullYear();
    // Only include workouts for the current month/year
    if (workoutMonth === currentDate.getMonth() && workoutYear === currentDate.getFullYear()) {
      const day = workoutDate.getDate();
      acc[day] = workout;
    }
    return acc;
  }, {});
  // Handle month selection
  const selectMonth = (month: number) => {
    // Ensure month is between 1-12
    if (month < 0 || month > 11) return;
    const newDate = new Date(currentDate);
    newDate.setMonth(month);
    setCurrentDate(newDate);
    // Clear selections when changing month
    setSelectedDays([]);
  };
  // Handle year selection
  const selectYear = (year: number) => {
    // Ensure year is between 1900-2025
    if (year < 1900 || year > 2025) return;
    const newDate = new Date(currentDate);
    newDate.setFullYear(year);
    setCurrentDate(newDate);
    // Clear selections when changing year
    setSelectedDays([]);
  };
  // Toggle day selection
  const toggleDaySelection = (day: number) => {
    // Ensure day is between 1-31
    if (day < 1 || day > 31) return;
    if (selectedDays.includes(day)) {
      // Remove day if already selected
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      // Add day if not selected
      setSelectedDays([...selectedDays, day]);
    }
    // Navigate to home screen when a day is clicked
    if (navigateTo) {
      navigateTo('home');
    }
    // Call the onDayClick callback if provided
    if (onDayClick) {
      onDayClick(day);
    }
  };
  return <div className="p-6" data-id="element-830">
      {/* Month Picker */}
      <div className="relative mb-6" data-id="element-831">
        <button onClick={() => setShowMonthPicker(!showMonthPicker)} className="w-full py-2 px-4 rounded-lg bg-white border border-gray-200 flex justify-between items-center" data-id="element-832">
          <span className="font-medium" data-id="element-833">{formattedDate}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${showMonthPicker ? 'rotate-180' : ''}`} data-id="element-834">
            <path d="m6 9 6 6 6-6" data-id="element-835"></path>
          </svg>
        </button>
        {showMonthPicker && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 flex overflow-hidden" data-id="element-836">
            {/* Month selector */}
            <div className="w-1/2 border-r border-gray-100" data-id="element-837">
              <div className="p-2 bg-gray-50 text-center text-sm font-medium text-gray-500" data-id="element-838">
                Month (1-12)
              </div>
              <div className="relative h-48 overflow-hidden" data-id="element-839">
                <button className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-white to-transparent z-10 flex justify-center" data-id="element-840">
                  <ChevronUp size={18} data-id="element-841" />
                </button>
                <div className="h-full overflow-y-auto py-8 px-2" data-id="element-842">
                  {months.map((month, index) => <div key={month} onClick={() => selectMonth(index)} className={`py-2 px-3 rounded-md text-center cursor-pointer ${index === currentDate.getMonth() ? 'bg-gray-800 text-white font-medium' : 'hover:bg-gray-100'}`} data-id="element-843">
                      {index + 1} - {month}
                    </div>)}
                </div>
                <button className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent z-10 flex justify-center" data-id="element-844">
                  <ChevronDown size={18} data-id="element-845" />
                </button>
              </div>
            </div>
            {/* Year selector */}
            <div className="w-1/2" data-id="element-846">
              <div className="p-2 bg-gray-50 text-center text-sm font-medium text-gray-500" data-id="element-847">
                Year (1900-2025)
              </div>
              <div className="relative h-48 overflow-hidden" data-id="element-848">
                <button className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-white to-transparent z-10 flex justify-center" data-id="element-849">
                  <ChevronUp size={18} data-id="element-850" />
                </button>
                <div className="h-full overflow-y-auto py-8 px-2" data-id="element-851">
                  {years.map(year => <div key={year} onClick={() => selectYear(year)} className={`py-2 px-3 rounded-md text-center cursor-pointer ${year === currentDate.getFullYear() ? 'bg-gray-800 text-white font-medium' : 'hover:bg-gray-100'}`} data-id="element-852">
                      {year}
                    </div>)}
                </div>
                <button className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent z-10 flex justify-center" data-id="element-853">
                  <ChevronDown size={18} data-id="element-854" />
                </button>
              </div>
            </div>
          </div>}
      </div>

      {/* Selected days summary */}
      {selectedDays.length > 0 && <div className="mb-4 p-3 bg-gray-100 rounded-lg" data-id="element-855">
          <div className="flex items-center justify-between" data-id="element-856">
            <div data-id="element-857">
              <span className="font-medium text-gray-700" data-id="element-858">
                {selectedDays.length} day{selectedDays.length !== 1 ? 's' : ''}{' '}
                selected
              </span>
              <p className="text-sm text-gray-600 mt-1" data-id="element-859">
                {selectedDays.sort((a, b) => a - b).map(day => `${day}`).join(', ')}
              </p>
            </div>
            <button onClick={() => setSelectedDays([])} className="text-gray-700 text-sm hover:underline" data-id="element-860">
              Clear
            </button>
          </div>
        </div>}

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-861">
        {days.map(day => <div key={day} className="text-center text-xs text-gray-500" data-id="element-862">
            {day}
          </div>)}
      </div>

      <div className="grid grid-cols-7 gap-1" data-id="element-863">
        {/* Empty cells for days before the 1st */}
        {Array(firstDayOfMonth).fill(0).map((_, i) => <div key={`empty-${i}`} className="h-12" data-id="element-864"></div>)}
        {calendarDays.map(day => {
        const workout = workoutsByDay[day];
        const isSelected = selectedDays.includes(day);
        let bgColor = 'bg-gray-100';
        if (isSelected) {
          bgColor = 'bg-gray-800 text-white';
        } else if (workout) {
          if (workout.completed) {
            bgColor = 'bg-green-100';
          } else if (workout.skipped) {
            bgColor = 'bg-red-100';
          } else {
            bgColor = 'bg-blue-100';
          }
        }
        return <div key={day} onClick={() => toggleDaySelection(day)} className={`h-12 rounded-lg ${bgColor} flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`} data-id="element-865">
              <div className={`text-sm font-medium ${isSelected ? 'text-white' : ''}`} data-id="element-866">
                {day}
              </div>
              {workout && !isSelected && <div className={`w-2 h-2 rounded-full mt-1 ${isSelected ? 'bg-white' : 'bg-gray-800'}`} data-id="element-867"></div>}
              {isSelected && <div className="w-2 h-2 rounded-full mt-1 bg-white" data-id="element-868"></div>}
            </div>;
      })}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4" data-id="element-869">
        <div className="flex items-center" data-id="element-870">
          <div className="w-4 h-4 rounded-full bg-green-100 mr-2" data-id="element-871"></div>
          <span className="text-xs text-gray-600" data-id="element-872">Completed</span>
        </div>
        <div className="flex items-center" data-id="element-873">
          <div className="w-4 h-4 rounded-full bg-blue-100 mr-2" data-id="element-874"></div>
          <span className="text-xs text-gray-600" data-id="element-875">Upcoming</span>
        </div>
        <div className="flex items-center" data-id="element-876">
          <div className="w-4 h-4 rounded-full bg-red-100 mr-2" data-id="element-877"></div>
          <span className="text-xs text-gray-600" data-id="element-878">Skipped</span>
        </div>
        <div className="flex items-center" data-id="element-879">
          <div className="w-4 h-4 rounded-full bg-gray-800 mr-2" data-id="element-880"></div>
          <span className="text-xs text-gray-600" data-id="element-881">Selected</span>
        </div>
      </div>
    </div>;
};